import type { DigestRecommendationsRawPayload, Proposal } from '../shared-types'
import { isDigestRecommendationsRawSuccessPayload } from '../shared-types'
import type { RecommendationsState } from './types'
import { PROPOSAL_PRIORITY, SOURCE_TO_PRIORITY } from '../constants'

import { defineStore } from 'pinia'

import { SfLog } from '@app/adapters/logger'
import { useCommon } from './common'
import { showErrorBanner } from '@app/application/account-mapping/utils'
import type { AccountMappingState } from './utils/mapping-priority-resolver'
import { canAIOverride } from './utils/mapping-priority-resolver'

/**
 * Merges proposals from multiple sources in priority order
 * @param internalOrExternalProposal - Proposal from Silverfin backend
 * @param aiProposals - Array of AI-generated proposals
 * @returns Array of Proposal objects with accountRange and source, sorted by priority with duplicates removed
 */
export function mergeProposalsWithPriority(
  internalOrExternalProposal: Proposal | null,
  aiProposals: Array<Proposal>
): Array<Proposal> {
  const proposals: Array<Proposal> = []

  if (internalOrExternalProposal) {
    proposals.push(internalOrExternalProposal)
  }

  // Filter out any malformed proposals that don't have required fields
  const validAiProposals = aiProposals.filter(
    (p) => p && p.accountRange && typeof p.source === 'string'
  )
  proposals.push(...validAiProposals)

  // Sort by priority based on source tag
  proposals.sort((a, b) => {
    const priorityA = SOURCE_TO_PRIORITY[a.source] || PROPOSAL_PRIORITY.NONE
    const priorityB = SOURCE_TO_PRIORITY[b.source] || PROPOSAL_PRIORITY.NONE
    return priorityA - priorityB
  })

  // Deduplicate by accountRange, keeping first (highest priority)
  const seen = new Set<string>()
  return proposals.filter((p) => {
    if (seen.has(p.accountRange)) return false
    seen.add(p.accountRange)
    return true
  })
}

export const useRecommendations = defineStore('accountMappingRecommendations', {
  state: (): RecommendationsState => ({
    isWaitingForRecommendations: false,
    recommendationError: '',
  }),
  actions: {
    autoMapRecommendations() {
      const commonStore = useCommon()

      Object.values(commonStore.$state.recommendations).forEach(
        ({ id, proposals }) => {
          if (proposals && proposals.length > 0) {
            const account = commonStore.$state.accounts[id]
            const mappingState: AccountMappingState = {
              isMapped: !!commonStore.getMappedAccountIds[id],
              isSaved: commonStore.getFlatMappedAccounts[id]?.isSaved || false,
              remappedByUser: !!account?.remappedByUser,
              proposalSource: account?.internalOrExternalProposal?.source,
            }

            if (!canAIOverride(mappingState)) {
              SfLog.info(
                'AI recommendation skipped: account mapping has higher priority',
                {
                  extra: {
                    accountId: id,
                    proposalSource: mappingState.proposalSource,
                    isSaved: mappingState.isSaved,
                    aiProposal: proposals[0].accountRange,
                  },
                }
              )
            }

            commonStore.addAccountToGroupFromRecommendations({
              accountRange: proposals[0].accountRange,
              accountId: id,
            })
          }
        }
      )
    },
    async digestRecommendations(data: DigestRecommendationsRawPayload) {
      const commonStore = useCommon()

      if (isDigestRecommendationsRawSuccessPayload(data)) {
        // Process AI recommendations from MessageBus
        const mergedRecommendations = data.accounts.map((aiRec) => {
          // Convert to string to match account store keys (see formatResponseData.ts:84)
          const accountId = String(aiRec.id)
          const account = commonStore.$state.accounts[accountId]

          if (!account) {
            SfLog.info('AI recommendation received for unknown account ID', {
              extra: { accountId },
            })
          }

          const internalOrExternalProposal =
            account?.internalOrExternalProposal || null

          const aiProposals: Array<Proposal> = aiRec.proposals.map((p) => ({
            accountRange: p,
            source: 'ai',
          }))

          const mergedProposals = mergeProposalsWithPriority(
            internalOrExternalProposal,
            aiProposals
          )

          return {
            id: accountId,
            proposals: mergedProposals,
            low_confidence: aiRec.low_confidence,
          }
        })

        commonStore.receiveRecommendations({
          accounts: mergedRecommendations,
          status: 'success',
          correlation_id: data.correlation_id,
        })

        if (!commonStore.$state.isFetching) {
          this.autoMapRecommendations()
        }
      } else {
        showErrorBanner(data.error_message, data.error_message_per_locale)

        await commonStore.fetchInitialData({
          shouldUsePostfixes: commonStore.$state.shouldUsePostfixes,
        })

        SfLog.info('Failed receiving account mapping recommendations', {
          extra: {
            error: data.error_message,
          },
        })
      }

      this.stopWaitingForRecommendations()
    },
    startWaitingForRecommendations() {
      this.isWaitingForRecommendations = true
    },
    stopWaitingForRecommendations() {
      this.isWaitingForRecommendations = false
    },
  },
})
